import { LocalDate } from '@js-joda/core';

import {
   ExternalSystemBusinessFault,
   FiscalDocumentProviderAuthorizationFault,
   ReceiptCreateCommand,
} from '@dreryk/egabinet-invoice-frontend-api';
import { Money } from '@dreryk/money-common-api';
import { TaxIdentifier } from '@dreryk/tax-identifier-common-api';

import { useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { createInterpolatedTranslationKey } from '@dreryk/react-common';
import { dispatchSnackbarNegativeEvent } from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { invoiceCreateModalTranslationKeys } from '../../translations';
import { InvoiceCreateFormBean } from '../form/InvoiceCreateForm.types';
import { getMedicalServiceName } from '../form/servicesSection/InvoiceItemsTable.utils';
import { InvoicePaymentMethod } from '../../types';

type PrivateIndividualInvoiceCreateCommandOptions = {
   onSuccess: (parameters: InvoiceCreateFormBean) => void;
   taxIdentifier?: TaxIdentifier;
   visitDate: LocalDate;
   visitSettlementId: number;
   paymentMethod: InvoicePaymentMethod;
};

export const usePrivateIndividualInvoiceCreateWithReceiptCommand = (
   options: PrivateIndividualInvoiceCreateCommandOptions
): UseCqrsCommandResult<
   void,
   InvoiceCreateFormBean,
   FiscalDocumentProviderAuthorizationFault | ExternalSystemBusinessFault
> =>
   useCqrsCommand<void, InvoiceCreateFormBean, FiscalDocumentProviderAuthorizationFault | ExternalSystemBusinessFault>(
      (parameters) => {
         const command = new ReceiptCreateCommand();
         command.fiscalItems = parameters.invoiceItems.map((item) => ({
            name: item.invoiceParameters.nameForReceipt ?? getMedicalServiceName(item.name)!,
            quantity: item.quantity!,
            unit: item.unit!,
            unitPrice: Money.of(item.unitPrice!),
            taxRate: item.taxRate!,
         }));
         command.sellDate = options.visitDate;
         command.referenceId = options.visitSettlementId.toString();
         command.taxIdentifier = Choice.withUseDefaultIfNull(options.taxIdentifier ?? null);
         command.paymentMethod = options.paymentMethod.type;
         command.paymentMethodDescription = Choice.withUseDefaultIfNull(options.paymentMethod.description ?? null);

         return command;
      },
      {
         onSuccess: (__, parameters) => options.onSuccess(parameters),
         onFault: {
            'egabinet-invoice-frontend-api:FiscalDocumentProviderAuthorizationFault': () => {
               dispatchSnackbarNegativeEvent(invoiceCreateModalTranslationKeys.apiTokenFault);
            },

            'egabinet-invoice-frontend-api:ExternalSystemBusinessFault': ({ externalSystemErrorMessage }) => {
               dispatchSnackbarNegativeEvent(
                  createInterpolatedTranslationKey(invoiceCreateModalTranslationKeys.externalSystemBusinessFault, {
                     messageFromFakturownia: externalSystemErrorMessage,
                  })
               );
            },
         },
      }
   );
